import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import { Card, Icon, Tooltip, CircularProgress } from "@mui/material";

// Custom UI components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

// Width calculator utility
import { useCalculatedWidths } from "./widthCalculator";

function DataTableCustom({
	columns,
	data,
	loading,
	loadingActions,
	editingRow,
	onEdit,
	onSave,
	onCancel,
	onDelete,
	renderEditCell,
	actions,
	showActions = true,
	emptyMessage = "No hay datos disponibles",
	loadingMessage = "Cargando datos...",
	maxHeight = "400px",
	showFooter = true,
	footerContent,
	customRowStyles,
	rowPaddingY = 1.5,
	onRowClick,
	zebraStripes = true,
	hover = true,
	border = true,
	showBorders = true,
	showVerticalBorders = showBorders,
	showHorizontalBorders = showBorders,
	headerStyles,
	// Nuevos props para ordenamiento
	sortable = true,
	defaultSort = null,
	onSortChange,
	// Props para control de anchos
	actionsWidth = "10%",
	autoCalculateWidths = true,
	// Expandibles
	expandedRowRender,
	rowExpandable,
	expandedKeys,
	onExpandChange,
	getRowId,
	expandColumnWidth = "40px",
	styleCard = {},
}) {
	// Calcular anchos automáticamente si está habilitado
	const { columns: processedColumns, actionsWidth: finalActionsWidth, info } =
		autoCalculateWidths
			? useCalculatedWidths(columns, showActions, actionsWidth)
			: { columns, actionsWidth, info: { totalWidthStyle: "100%" } };

	const finalColumns = autoCalculateWidths ? processedColumns : columns;

	const [internalSort, setInternalSort] = useState(defaultSort ?? { field: null, direction: null });

	useEffect(() => {
		if (!onSortChange) {
			setInternalSort(defaultSort ?? { field: null, direction: null });
		}
	}, [defaultSort?.field, defaultSort?.direction, onSortChange]);

	const currentSort = (onSortChange ? defaultSort : internalSort) ?? { field: null, direction: null };

	const [internalExpandedKeys, setInternalExpandedKeys] = useState(expandedKeys ?? []);

	const dividerColor = "1px solid rgba(0,0,0,0.08)";

	useEffect(() => {
		if (expandedKeys != null) {
			setInternalExpandedKeys(expandedKeys);
		}
	}, [expandedKeys]);

	const computedHeaderStyles = useMemo(() => {
		const { backgroundColor, borderBottom, ...rest } = headerStyles || {};
		return {
			backgroundColor: backgroundColor ?? "#ffffff",
			borderBottom: showHorizontalBorders ? borderBottom ?? dividerColor : "none",
			...rest,
		};
	}, [headerStyles, dividerColor, showHorizontalBorders]);

	const handleSortChange = (field) => {
		const column = finalColumns.find((col) => col.field === field);
		if (column?.sortable === false) return;

		let newDirection = "asc";
		if (currentSort?.field === field) {
			if (currentSort.direction === "asc") {
				newDirection = "desc";
			} else if (currentSort.direction === "desc") {
				newDirection = null;
			} else {
				newDirection = "asc";
			}
		}

		const newSort = newDirection ? { field, direction: newDirection } : { field: null, direction: null };

		if (onSortChange) {
			onSortChange(newSort);
		} else {
			setInternalSort(newSort);
		}
	};

	// Función para ordenar los datos
	const sortedData = useMemo(() => {
		if (!currentSort?.field || !currentSort?.direction) {
			return data;
		}

		const column = finalColumns.find((col) => col.field === currentSort.field);
		if (!column) return data;

		return [...data].sort((a, b) => {
			let aValue = a[currentSort.field];
			let bValue = b[currentSort.field];

			// Si la columna tiene una función customSortValue, usarla
			if (column.customSortValue) {
				aValue = column.customSortValue(a);
				bValue = column.customSortValue(b);
			}

			// Manejar valores nulos/undefined
			if (aValue == null && bValue == null) return 0;
			if (aValue == null) return 1;
			if (bValue == null) return -1;

			// Convertir a string para comparación si no son números
			if (typeof aValue !== "number" && typeof bValue !== "number") {
				aValue = String(aValue).toLowerCase();
				bValue = String(bValue).toLowerCase();
			}

			let result = 0;
			if (aValue < bValue) result = -1;
			else if (aValue > bValue) result = 1;

			return currentSort.direction === "desc" ? -result : result;
		});
	}, [data, currentSort, finalColumns]);
	// Renderizar una fila individual (con soporte expandible)
	const renderTableRow = useMemo(() => (item, index) => {
		const key = getRowId ? getRowId(item, index) : (item.id ?? index);
		const isEditing = editingRow === (getRowId ? getRowId(item, index) : item.id);
		const canExpand = typeof rowExpandable === 'function' ? rowExpandable(item) : Boolean(expandedRowRender);
		const currentExpandedKeys = expandedKeys ?? internalExpandedKeys;
		const isExpanded = currentExpandedKeys.includes(key);

		const toggleExpand = () => {
			if (!canExpand || !expandedRowRender) return;
			const next = isExpanded
				? currentExpandedKeys.filter((k) => k !== key)
				: [...currentExpandedKeys, key];
			if (onExpandChange) onExpandChange(next, item);
			if (expandedKeys == null) setInternalExpandedKeys(next);
		};

		return (
			<React.Fragment key={key}>
				<SoftBox
					display="flex"
					alignItems="center"
					py={rowPaddingY}
					px={0}
					onClick={onRowClick ? () => onRowClick(item) : undefined}
					onKeyDown={onRowClick ? (event) => {
						if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
							event.preventDefault();
							onRowClick(item);
						}
					} : undefined}
					role={onRowClick ? "button" : undefined}
					tabIndex={onRowClick ? 0 : undefined}
					aria-expanded={onRowClick && expandedRowRender ? isExpanded : undefined}
						sx={{
						backgroundColor: zebraStripes && index % 2 === 0 ? "transparent" : "rgba(0,0,0,0.02)",
						"&:hover": hover ? {
							backgroundColor: "rgba(0,0,0,0.05)",
						} : {},
						borderBottom: border && showHorizontalBorders ? dividerColor : "none",
						cursor: onRowClick ? "pointer" : "default",
						"&:focus-visible": onRowClick ? {
							outline: "2px solid #635bff",
							outlineOffset: "-2px",
						} : {},
						minWidth: info?.totalWidthStyle || "max-content",
						...customRowStyles?.(item, index, isEditing),
					}}
				>
					{/* Columna de expandir */}
					{expandedRowRender ? (
						<SoftBox
							px={2}
							display="flex"
							alignItems="center"
							justifyContent="center"
							sx={{
								boxSizing: "border-box",
								flex: `0 0 ${expandColumnWidth}`,
								maxWidth: expandColumnWidth,
								minWidth: expandColumnWidth,
								borderRight: showVerticalBorders ? dividerColor : "none",
							}}
						>
							{canExpand ? (
								<Tooltip title={isExpanded ? "Contraer" : "Expandir"}>
									<SoftButton
										variant="text"
										color="dark"
										size="small"
										iconOnly
										circular
										onClick={(e) => {
											e.stopPropagation();
											toggleExpand();
										}}
									>
										<Icon fontSize="small">{isExpanded ? 'expand_less' : 'expand_more'}</Icon>
									</SoftButton>
								</Tooltip>
							) : (
								<SoftBox sx={{ width: 24 }} />
							)}
						</SoftBox>
					) : null}

					{(() => {
						let stickyLeft = expandedRowRender ? getStickyOffset(expandColumnWidth) : 0;
						return finalColumns.map((column) => {
							const isStickyLeft = column.sticky === "left";
							const cellLeft = isStickyLeft ? `${stickyLeft}px` : undefined;
							if (isStickyLeft && column.width) {
								stickyLeft += getStickyOffset(column.width);
							}

							return (
								<SoftBox
									key={column.field}
									px={2}
									display="flex"
									alignItems="center"
									justifyContent={column.align === "center" ? "center" : column.align === "right" ? "flex-end" : "flex-start"}
									sx={{
										boxSizing: "border-box",
										paddingLeft: column.paddingLeft,
										// A column can opt into growing from its content-based width while keeping that width as its non-shrinking basis.
										flex: column.width ? `${column.grow || 0} 0 ${column.width}` : "1 1 0%",
										maxWidth: column.grow ? "none" : column.width || "none",
										minWidth: 0, // Permite que el contenido se comprima
										overflow: "hidden", // Oculta el contenido que se desborda
										borderRight: showVerticalBorders ? dividerColor : "none",
										position: isStickyLeft ? "sticky" : "static",
										left: cellLeft,
										zIndex: isStickyLeft ? 3 : "auto",
										backgroundColor: isStickyLeft ? "#ffffff" : "transparent",
										boxShadow: isStickyLeft && showVerticalBorders ? "2px 0 0 rgba(0,0,0,0.08)" : "none",
									}}
								>
									{isEditing && column.editable ? (
										renderEditCell ? renderEditCell(column, item) : (
											<SoftTypography
												variant="button"
												fontWeight="regular"
												color="secondary"
												sx={{
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													maxWidth: "100%",
													textAlign: column.align === "center" ? "center" : column.align === "right" ? "right" : "left",
												}}
											>
												{column.renderCell ? column.renderCell(item, index) : item[column.field]}
											</SoftTypography>
										)
									) : (
										<SoftBox
											sx={{
												width: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
												minWidth: 0,
											}}
										>
											{column.renderCell ? (
												<SoftBox
													sx={{
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
														maxWidth: "100%",
														textAlign: column.align === "center" ? "center" : column.align === "right" ? "right" : "left",
													}}
													title={["string", "number"].includes(typeof item[column.field]) ? String(item[column.field]) : undefined}
												>
													{column.renderCell(item, index)}
												</SoftBox>
											) : (
												<SoftTypography
													variant="button"
													fontWeight={column.fontWeight || "regular"}
													color={column.color || "dark"}
													sx={{
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
														maxWidth: "100%",
														display: "block",
														textAlign: column.align === "center" ? "center" : column.align === "right" ? "right" : "left",
													}}
													title={["string", "number"].includes(typeof item[column.field]) ? String(item[column.field]) : undefined} // Tooltip con texto completo
												>
													{item[column.field] || "-"}
												</SoftTypography>
											)}
										</SoftBox>
									)}
								</SoftBox>
							);
						});
					})()}

					{/* Columna de acciones */}
					{showActions && (
						<SoftBox
							display="flex"
							gap={1}
							justifyContent="center"
							px={2}
							sx={{
								boxSizing: "border-box",
								flex: finalActionsWidth ? `0 0 ${finalActionsWidth}` : "0 0 10%",
								maxWidth: finalActionsWidth || "none",
							}}
						>
							{isEditing ? (
								<>
									<Tooltip title="Guardar">
										<SoftButton
											variant="outlined"
											color="success"
											size="small"
											iconOnly
											circular
											disabled={loadingActions}
											onClick={() => onSave && onSave(item)}
										>
											<Icon fontSize="small">save</Icon>
										</SoftButton>
									</Tooltip>
									<Tooltip title="Cancelar">
										<SoftButton
											variant="outlined"
											color="error"
											size="small"
											iconOnly
											circular
											disabled={loadingActions}
											onClick={() => onCancel && onCancel()}
										>
											<Icon fontSize="small">close</Icon>
										</SoftButton>
									</Tooltip>
								</>
							) : (
								<>
									{(actions?.map((action, actionIndex) => {
										if (action.hidden === true || action.hidden?.(item)) {
											return null;
										}

										const tooltipValue =
											typeof action.tooltip === "function"
												? action.tooltip(item)
												: action.tooltip ??
												(typeof action.label === "function" ? action.label(item) : action.label);

										const labelValue =
											typeof action.label === "function" ? action.label(item) : action.label;

										const colorValue =
											typeof action.color === "function" ? action.color(item) : action.color || "dark";

										const variantValue =
											typeof action.variant === "function"
												? action.variant(item)
												: action.variant || "outlined";

										const sizeValue =
											typeof action.size === "function" ? action.size(item) : action.size || "small";

										const iconOnlyValue =
											typeof action.iconOnly === "function"
												? action.iconOnly(item)
												: action.iconOnly !== false;

										const circularValue =
											typeof action.circular === "function"
												? action.circular(item)
												: action.circular !== false;

										const isDisabled = action.disabled?.(item) || loadingActions;

										const iconValue =
											typeof action.icon === "function" ? action.icon(item) : action.icon;

										const iconContent =
											React.isValidElement(iconValue) ? (
												iconValue
											) : (
												<Icon fontSize="small">{iconValue ?? "help_outline"}</Icon>
											);

										return (
											<Tooltip key={actionIndex} title={tooltipValue || labelValue}>
												<SoftButton
													variant={variantValue}
													color={colorValue}
													size={sizeValue}
													iconOnly={iconOnlyValue}
													circular={circularValue}
													onClick={() => !isDisabled && action.onClick && action.onClick(item)}
													disabled={isDisabled}
												>
													{iconContent}
												</SoftButton>
											</Tooltip>
										);
									})) || (
											<>
												<Tooltip title="Editar">
													<SoftButton
														variant="outlined"
														color="dark"
														size="small"
														iconOnly
														circular
														disabled={loadingActions}
														onClick={() => onEdit && onEdit(item)}
													>
														<Icon fontSize="small">edit</Icon>
													</SoftButton>
												</Tooltip>
												<Tooltip title="Eliminar">
													<SoftButton
														variant="outlined"
														color="error"
														size="small"
														iconOnly
														circular
														disabled={loadingActions}
														onClick={() => onDelete && onDelete(item)}
													>
														<Icon fontSize="small">delete</Icon>
													</SoftButton>
												</Tooltip>
											</>
										)}
								</>
							)}
						</SoftBox>
					)}
				</SoftBox>

				{/* Contenido expandido */}
				{expandedRowRender && isExpanded && (
					<SoftBox key={`expanded_${key}`} px={2} pb={1} sx={{ borderBottom: border && showHorizontalBorders ? dividerColor : "none" }}>
						{expandedRowRender(item, index)}
					</SoftBox>
				)}
			</React.Fragment>
		);
	}, [
		editingRow,
		finalColumns,
		dividerColor,
		onEdit,
		onSave,
		onCancel,
		onDelete,
		renderEditCell,
		actions,
		showActions,
		onRowClick,
		zebraStripes,
		hover,
		border,
		showVerticalBorders,
		showHorizontalBorders,
		customRowStyles,
		rowPaddingY,
		expandedKeys,
		internalExpandedKeys,
		expandedRowRender,
		rowExpandable,
		onExpandChange,
		getRowId,
		expandColumnWidth,
		loadingActions,
	]);

	const getStickyOffset = (value) => {
		if (typeof value === "number") return value;
		if (typeof value === "string" && value.endsWith("px")) {
			const parsed = Number(value.replace("px", ""));
			return Number.isNaN(parsed) ? 0 : parsed;
		}
		return 0;
	};

	// Renderizar el header de la tabla
	const tableHeader = useMemo(() => {
		const { backgroundColor: headerBg, borderBottom: headerBorder, ...headerRest } = computedHeaderStyles;
		let stickyLeft = expandedRowRender ? getStickyOffset(expandColumnWidth) : 0;

		return (
			<SoftBox
				display="flex"
				alignItems="center"
				py={2}
				px={0}
				sx={{
					backgroundColor: headerBg,
					borderBottom: headerBorder,
					fontWeight: "bold",
					boxShadow: showHorizontalBorders ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
					...headerRest,
					position: "sticky",
					top: 0,
					zIndex: 5,
					minWidth: info?.totalWidthStyle || "max-content",
				}}
			>
				{/* Celda vacía para alineación del expander */}
				{expandedRowRender ? (
					<SoftBox
						px={2}
						display="flex"
						alignItems="center"
						justifyContent="center"
						sx={{
							boxSizing: "border-box",
							flex: `0 0 ${expandColumnWidth}`,
							maxWidth: expandColumnWidth,
							minWidth: expandColumnWidth,
							borderRight: showVerticalBorders ? dividerColor : "none",
						}}
					/>
				) : null}
				{finalColumns.map((column) => {
					const isSortable = sortable && column.sortable !== false;
					const isCurrentSort = currentSort?.field === column.field;
					const isStickyLeft = column.sticky === "left";
					const headerLeft = isStickyLeft ? `${stickyLeft}px` : undefined;
					if (isStickyLeft && column.width) {
						stickyLeft += getStickyOffset(column.width);
					}

					return (
						<SoftBox
							key={column.field}
							px={2}
							display="flex"
							alignItems="center"
							justifyContent={column.align === "center" ? "center" : column.align === "right" ? "flex-end" : "flex-start"}
							onClick={isSortable ? () => handleSortChange(column.field) : undefined}
							onKeyDown={isSortable ? (event) => {
								if (event.key === "Enter" || event.key === " ") {
									event.preventDefault();
									handleSortChange(column.field);
								}
							} : undefined}
							role={isSortable ? "button" : undefined}
							tabIndex={isSortable ? 0 : undefined}
							aria-label={isSortable ? `Ordenar por ${column.headerName || column.label}` : undefined}
							sx={{
								boxSizing: "border-box",
								paddingLeft: column.paddingLeft,
								cursor: isSortable ? "pointer" : "default",
								"&:hover": isSortable ? {
									backgroundColor: "rgba(0,0,0,0.05)",
									borderRadius: "4px",
								} : {},
								"&:focus-visible": isSortable ? {
									outline: "2px solid #635bff",
									outlineOffset: "-2px",
									borderRadius: "4px",
								} : {},
								transition: "background-color 0.2s ease",
								// Use the same grow and basis values as the body so header and cells remain aligned.
								flex: column.width ? `${column.grow || 0} 0 ${column.width}` : "1 1 0%",
								maxWidth: column.grow ? "none" : column.width || "none",
								minWidth: 0, // Permite que el contenido se comprima
								overflow: "hidden", // Oculta el contenido que se desborda
								borderRight: showVerticalBorders ? dividerColor : "none",
								position: isStickyLeft ? "sticky" : "static",
								left: headerLeft,
								zIndex: isStickyLeft ? 6 : "auto",
								backgroundColor: isStickyLeft ? headerBg : "inherit",
								boxShadow: isStickyLeft && showVerticalBorders ? "2px 0 0 rgba(0,0,0,0.08)" : "none",
							}}
						>
							{/* Renderizar el header personalizado o el texto por defecto */}
							{column.renderHeader ? (
								<SoftBox
									sx={{
										width: "100%",
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										minWidth: 0,
										textAlign: column.align === "center" ? "center" : column.align === "right" ? "right" : "left",
									}}
								>
									{column.renderHeader(column, isCurrentSort, currentSort?.direction)}
								</SoftBox>
							) : (
								<SoftBox
									display="flex"
									alignItems="center"
									gap={0.5}
									sx={{
										width: "100%",
										overflow: "hidden",
										minWidth: 0,
									}}
								>
									<SoftTypography
										variant="button"
										fontWeight="bold"
										textTransform="uppercase"
										color={column.headerColor || "dark"}
										sx={{
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											flex: 1,
											minWidth: 0,
											textAlign: column.align === "center" ? "center" : column.align === "right" ? "right" : "left",
										}}
										title={column.label || column.headerName} // Tooltip con texto completo
									>
										{column.headerName || column.label}
									</SoftTypography>

									{/* Indicador de ordenamiento */}
									{isSortable && (
										<SoftBox display="flex" flexDirection="column" flexShrink={0}>
											{isCurrentSort && currentSort.direction ? (
												<Icon
													fontSize="small"
													sx={{
														color: currentSort.direction === 'asc' ? 'primary.main' : 'primary.main',
														opacity: 0.8
													}}
												>
													{currentSort.direction === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
												</Icon>
											) : (
												<Icon
													fontSize="small"
													sx={{
														color: 'text.secondary',
														opacity: 0.4,
														'&:hover': { opacity: 0.8 }
													}}
												>
													import_export
												</Icon>
											)}
										</SoftBox>
									)}
								</SoftBox>
							)}
						</SoftBox>
					);
				})}

				{showActions && (
					<SoftBox
						display="flex"
						justifyContent="center"
						px={2}
						sx={{
							boxSizing: "border-box",
							flex: finalActionsWidth ? `0 0 ${finalActionsWidth}` : "0 0 10%",
							maxWidth: finalActionsWidth || "none",
						}}
					>
						<SoftTypography
							variant="caption"
							fontWeight="bold"
							textTransform="uppercase"
							color="dark"
							ml={2}
							sx={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								// flex: 1,
								minWidth: 0,
							}}
						>
							Acciones
						</SoftTypography>
					</SoftBox>
				)}
			</SoftBox>
		);
	}, [finalColumns, showActions, computedHeaderStyles, sortable, currentSort, handleSortChange, finalActionsWidth, expandedRowRender, expandColumnWidth, dividerColor, showVerticalBorders, showHorizontalBorders]);

	if (loading) {
		return (
			<Card sx={{ overflow: "hidden", border: border ? "1px solid rgba(0,0,0,0.1)" : "none" }}>
				<SoftBox
					display="flex"
					flexDirection="column"
					justifyContent="center"
					alignItems="center"
					p={5}
					minHeight="200px"
				>
					<CircularProgress size={40} />
					<SoftTypography variant="body2" color="text" mt={2}>
						{loadingMessage}
					</SoftTypography>
				</SoftBox>
			</Card>
		);
	}

	if (!data || data.length === 0) {
		return (
			<Card sx={{ overflow: "hidden", border: border ? "1px solid rgba(0,0,0,0.1)" : "none" }}>
				<SoftBox
					display="flex"
					justifyContent="center"
					alignItems="center"
					p={5}
					minHeight="200px"
				>
					<SoftTypography variant="body1" color="text">
						{emptyMessage}
					</SoftTypography>
				</SoftBox>
			</Card>
		);
	}

	return (
		<Card sx={{
			overflow: "hidden",
			border: border ? "1px solid rgba(0,0,0,0.1)" : "none",
			...styleCard
		}}>
			<SoftBox
				sx={{
					maxHeight,
					overflowY: "auto",
					overflowX: "auto",
					position: "relative",
					"&::-webkit-scrollbar": {
						width: "8px",
						height: "8px",
					},
					"&::-webkit-scrollbar-track": {
						background: "#f1f1f1",
					},
					"&::-webkit-scrollbar-thumb": {
						background: "#c1c1c1",
						borderRadius: "4px",
					},
					"&::-webkit-scrollbar-thumb:hover": {
						background: "#a8a8a8",
					},
				}}
			>
				<SoftBox sx={{ minWidth: info?.totalWidthStyle || "max-content" }}>
					{tableHeader}
					{sortedData.map((item, index) => renderTableRow(item, index))}
				</SoftBox>
			</SoftBox>

			{/* Footer */}
			{showFooter && (
				<SoftBox
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					p={2}
					sx={{
						backgroundColor: "rgba(0,0,0,0.02)",
						borderTop: "1px solid rgba(0,0,0,0.1)",
					}}
				>
					{footerContent ? (
						footerContent
					) : (
						<>
							<SoftTypography variant="caption" color="text">
								Total de registros: {sortedData.length}
							</SoftTypography>
							<SoftTypography variant="caption" color="text">
								{editingRow ? "Editando..." : "Listo"}
							</SoftTypography>
						</>
					)}
				</SoftBox>
			)}
		</Card>
	);
}

// Configuración de PropTypes
DataTableCustom.propTypes = {
	// Configuración básica
	columns: PropTypes.arrayOf(
		PropTypes.shape({
			field: PropTypes.string.isRequired,
			headerName: PropTypes.string,
			label: PropTypes.string,
			width: PropTypes.string,
			grow: PropTypes.number,
			align: PropTypes.oneOf(["left", "center", "right"]),
			editable: PropTypes.bool,
			renderCell: PropTypes.func,
			renderHeader: PropTypes.func, // Nueva prop para header personalizado
			fontWeight: PropTypes.string,
			color: PropTypes.string,
			headerColor: PropTypes.string,
			sortable: PropTypes.bool, // Nueva prop para controlar ordenamiento por columna
			customSortValue: PropTypes.func, // Nueva prop para valor de ordenamiento personalizado
			sticky: PropTypes.oneOf(["left"]),
			paddingLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		})
	).isRequired,
	data: PropTypes.array.isRequired,

	// Estados de carga y edición
	loading: PropTypes.bool,
	editingRow: PropTypes.any,

	// Funciones de callback
	onEdit: PropTypes.func,
	onSave: PropTypes.func,
	onCancel: PropTypes.func,
	onDelete: PropTypes.func,
	onRowClick: PropTypes.func,
	renderEditCell: PropTypes.func,

	// Configuración de acciones
	actions: PropTypes.arrayOf(
		PropTypes.shape({
			icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.node]).isRequired,
			tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
			label: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
			onClick: PropTypes.func,
			color: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
			variant: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
			size: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
			iconOnly: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
			circular: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
			disabled: PropTypes.func,
			hidden: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
		})
	),
	showActions: PropTypes.bool,

	// Mensajes y contenido
	emptyMessage: PropTypes.string,
	loadingMessage: PropTypes.string,
	footerContent: PropTypes.node,

	// Estilos y apariencia
	maxHeight: PropTypes.string,
	showFooter: PropTypes.bool,
	customRowStyles: PropTypes.func,
	rowPaddingY: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	zebraStripes: PropTypes.bool,
	hover: PropTypes.bool,
	border: PropTypes.bool,
	showBorders: PropTypes.bool,
	showVerticalBorders: PropTypes.bool,
	showHorizontalBorders: PropTypes.bool,
	headerStyles: PropTypes.object,

	// Nuevas props para ordenamiento
	sortable: PropTypes.bool,
	defaultSort: PropTypes.shape({
		field: PropTypes.string,
		direction: PropTypes.oneOf(['asc', 'desc', null]),
	}),
	onSortChange: PropTypes.func,

	// Props para control de anchos
	actionsWidth: PropTypes.string,
	autoCalculateWidths: PropTypes.bool,

	// Expandibles
	expandedRowRender: PropTypes.func,
	rowExpandable: PropTypes.func,
	expandedKeys: PropTypes.array,
	onExpandChange: PropTypes.func,
	getRowId: PropTypes.func,
	expandColumnWidth: PropTypes.string,

	// Otros
	loadingActions: PropTypes.bool,
	styleCard: PropTypes.object,
};

export default DataTableCustom;
